import React, { useState } from 'react'
import { CreditCard, Euro, Smartphone, Building } from 'lucide-react'

import Button from '../ui/Button'
import Input from '../ui/Input'
import Select from '../ui/Select'

import { 
  PagamentoVendaFormData,
  formasPagamento,
  getFormaPagamentoConfig,
  formatarValor
} from '../../services/vendas'

interface PagamentoModalProps {
  valorPendente: number
  onAddPagamento: (pagamento: PagamentoVendaFormData) => void
  onCancel: () => void
}

const PagamentoModal: React.FC<PagamentoModalProps> = ({
  valorPendente,
  onAddPagamento,
  onCancel
}) => {
  const [formaPagamento, setFormaPagamento] = useState<PagamentoVendaFormData['formaPagamento']>('DINHEIRO')
  const [valor, setValor] = useState('')
  const [referencia, setReferencia] = useState('')
  const [observacoes, setObservacoes] = useState('')

  const config = getFormaPagamentoConfig(formaPagamento)

  // Função para validar valor com arredondamento correto (permite valor maior para calcular troco)
  const isValorValido = (valorInput: number) => {
    const valorArredondado = Math.round(valorInput * 100) / 100
    return valorArredondado > 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    console.log('🔥 INICIO handleSubmit')
    console.log('Estado atual:', { formaPagamento, valor, referencia, observacoes })

    const valorNumerico = typeof valor === 'string' ? parseFloat(valor) : valor
    console.log('Valor numérico:', valorNumerico)

    if (!isValorValido(valorNumerico)) {
      console.log('❌ Valor inválido:', valorNumerico)
      alert('Valor deve ser maior que zero')
      return
    }

    if (config.requiresReference && !referencia.trim()) {
      console.log('❌ Referência obrigatória mas não fornecida')
      alert('Referência é obrigatória para este tipo de pagamento')
      return
    }

    const pagamento: PagamentoVendaFormData = {
      formaPagamento,
      valor: valorNumerico,
      referencia: referencia.trim() || undefined,
      observacoes: observacoes.trim() || undefined
    }

    console.log('✅ Pagamento criado:', pagamento)
    console.log('🚀 Chamando onAddPagamento...')

    onAddPagamento(pagamento)

    console.log('✅ onAddPagamento chamado')

    // Resetar formulário
    setFormaPagamento('DINHEIRO')
    setValor('')
    setReferencia('')
    setObservacoes('')

    console.log('🔥 FIM handleSubmit')
  }

  const getIconForFormaPagamento = (forma: string) => {
    switch (forma) {
      case 'DINHEIRO':
        return <Euro className="h-5 w-5" />
      case 'MBWAY':
        return <Smartphone className="h-5 w-5" />
      case 'MULTIBANCO':
      case 'TRANSFERENCIA':
        return <Building className="h-5 w-5" />
      default:
        return <Euro className="h-5 w-5" />
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2">
          <span className="text-blue-600 font-medium">Valor pendente:</span>
          <span className="text-lg font-bold text-blue-900">{formatarValor(valorPendente)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Forma de Pagamento *"
          value={formaPagamento}
          onChange={(e) => setFormaPagamento(e.target.value as PagamentoVendaFormData['formaPagamento'])}
          options={formasPagamento.map(forma => ({
            value: forma.value,
            label: forma.label
          }))}
        />

        <Input
          label="Valor *"
          type="number"
          step="0.01"
          min="0.01"
          value={valor}
          onChange={(e) => {
            setValor(e.target.value)
          }}
          onBlur={(e) => {
            // Formatar valor ao sair do campo
            const inputValue = parseFloat(e.target.value)
            if (!isNaN(inputValue) && inputValue > 0) {
              setValor((Math.round(inputValue * 100) / 100).toString())
            }
          }}
          rightIcon={<span className="text-gray-500">€</span>}
        />
      </div>

      {config.requiresReference && (
        <Input
          label="Referência *"
          placeholder={`Referência do pagamento via ${config.label}`}
          value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
        />
      )}

      <Input
        label="Observações"
        placeholder="Observações sobre o pagamento (opcional)"
        value={observacoes}
        onChange={(e) => setObservacoes(e.target.value)}
      />

      {/* Preview do pagamento */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Resumo do Pagamento</h4>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            {getIconForFormaPagamento(formaPagamento)}
            <span className="font-medium text-gray-900">{config.label}</span>
          </div>
          <div className="text-lg font-bold text-green-600">
            {formatarValor(typeof valor === 'string' ? (parseFloat(valor) || 0) : valor)}
          </div>
          {referencia && (
            <div className="text-sm text-gray-600">
              Ref: {referencia}
            </div>
          )}
        </div>
        {observacoes && (
          <div className="mt-2 text-sm text-gray-600">
            <strong>Obs:</strong> {observacoes}
          </div>
        )}
      </div>

      {/* Botões */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          leftIcon={<CreditCard className="h-4 w-4" />}
          disabled={!isValorValido(typeof valor === 'string' ? (parseFloat(valor) || 0) : valor) || (config.requiresReference && !referencia.trim())}
        >
          Adicionar Pagamento
        </Button>
      </div>

      {/* Botões de valor rápido */}
      <div className="border-t border-gray-200 pt-4">
        <div className="text-sm font-medium text-gray-700 mb-2">Valores rápidos:</div>
        <div className="grid grid-cols-4 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValor((Math.round(valorPendente * 100) / 100).toString())}
          >
            Total
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValor((Math.round(valorPendente / 2 * 100) / 100).toString())}
          >
            Metade
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValor('20')}
          >
            €20
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValor('50')}
          >
            €50
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValor('100')}
          >
            €100
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValor('200')}
          >
            €200
          </Button>
        </div>
      </div>
    </form>
  )
}

export default PagamentoModal